/** Inactivité de la personne (docs/17 §4.5) : le navigateur verrouille la session après le délai choisi par l'unité
 *  (30 min par défaut ; 15 min, 1 h, 2 h ou 4 h) sans aucune activité dans aucun de ses onglets.
 *
 *  - Activité : clavier, pointeur (appui ; déplacement relevé au plus une fois par seconde), molette, défilement,
 *    toucher dans l'onglet.
 *  - Décompte repris à `visibilitychange` : l'iPad suspend les minuteries d'une page cachée.
 *  - Partagée entre les onglets, qui portent la même session : chaque onglet diffuse l'heure de sa dernière activité
 *    sur le canal `generex-auth`, au plus une fois toutes les 30 s, et l'écrit dans `localStorage` (`generex-activite`,
 *    sous try/catch). Un onglet caché et oublié ne verrouille donc pas la session de qui travaille dans un autre.
 *  - À l'échéance, et seulement si aucun onglet n'a eu d'activité dans le délai, `onIdle` (le portail appelle
 *    `POST /api/auth/lock`). Le serveur verrouille de lui-même au-delà du délai plus 5 min (onglets cachés, iPad en
 *    veille, portables refermés). */

/** Clé `localStorage` de la dernière activité, commune aux onglets du navigateur. */
export const ACTIVITY_KEY = "generex-activite";
/** Diffusion de l'activité aux autres onglets : au plus une fois toutes les 30 s. */
export const SHARE_EVERY_MS = 30_000;
/** Déplacement du pointeur : relevé au plus une fois par seconde. */
export const POINTER_EVERY_MS = 1000;

export interface IdleDeps {
  now: () => number;
  setTimer: (run: () => void, ms: number) => unknown;
  clearTimer: (id: unknown) => void;
  /** Diffusion de l'heure d'activité sur le canal. */
  share: (at: number) => void;
  /** Dernière activité écrite par un onglet (localStorage), ou null. */
  readShared: () => number | null;
  writeShared: (at: number) => void;
  /** Délai écoulé sans activité dans aucun onglet. */
  onIdle: () => void;
}

export class IdleTracker {
  private last: number;
  private lastShared = Number.NEGATIVE_INFINITY;
  private lastPointer = Number.NEGATIVE_INFINITY;
  private timer: unknown = null;
  private stopped = false;

  constructor(
    private limitMs: number,
    private readonly deps: IdleDeps,
  ) {
    this.last = deps.now();
    this.schedule(limitMs);
  }

  /** Délai de verrouillage (réglage de l'unité). */
  setLimit(limitMs: number): void {
    if (limitMs === this.limitMs) return;
    this.limitMs = limitMs;
    this.check();
  }

  /** Activité dans cet onglet ; `pointer` : déplacement du pointeur, relevé au plus une fois par seconde. */
  activity(kind: "pointer" | "other" = "other"): void {
    if (this.stopped) return;
    const now = this.deps.now();
    if (kind === "pointer") {
      if (now - this.lastPointer < POINTER_EVERY_MS) return;
      this.lastPointer = now;
    }
    this.last = Math.max(this.last, now);
    if (now - this.lastShared >= SHARE_EVERY_MS) {
      this.lastShared = now;
      this.deps.share(now);
      this.deps.writeShared(now);
    }
  }

  /** Activité d'un autre onglet (canal). */
  remote(at: number): void {
    if (Number.isFinite(at) && at > this.last) this.last = at;
  }

  /** Dernière activité connue, tous onglets confondus. */
  lastActivity(): number {
    const shared = this.deps.readShared();
    if (shared !== null && shared > this.last) this.last = shared;
    return this.last;
  }

  /** Décompte : verrouillage si le délai est écoulé, sinon nouvelle échéance (aussi au retour de la page). */
  check(): void {
    if (this.stopped) return;
    const idle = this.deps.now() - this.lastActivity();
    if (idle >= this.limitMs) {
      this.stop();
      this.deps.onIdle();
    } else this.schedule(this.limitMs - idle);
  }

  /** Reprise après un déverrouillage : le décompte repart de maintenant. */
  restart(): void {
    this.stopped = false;
    this.last = this.deps.now();
    this.schedule(this.limitMs);
  }

  stop(): void {
    this.stopped = true;
    if (this.timer !== null) this.deps.clearTimer(this.timer);
    this.timer = null;
  }

  private schedule(ms: number) {
    if (this.timer !== null) this.deps.clearTimer(this.timer);
    // Échéance un peu après le délai : une activité d'un autre onglet arrivée entre-temps la repousse.
    this.timer = this.deps.setTimer(() => {
      this.timer = null;
      this.check();
    }, Math.max(1000, ms + 250));
  }
}

function readActivity(): number | null {
  try {
    const value = Number(window.localStorage.getItem(ACTIVITY_KEY));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
}

function writeActivity(at: number): void {
  try {
    window.localStorage.setItem(ACTIVITY_KEY, String(at));
  } catch {
    /* stockage refusé : le canal suffit */
  }
}

/** Décompte d'inactivité de ce document : écoute de l'activité et de la visibilité ; rend le décompte et sa fin. */
export function installIdle(limitMs: number, share: (at: number) => void, onIdle: () => void): { tracker: IdleTracker; dispose: () => void } {
  const tracker = new IdleTracker(limitMs, {
    now: Date.now,
    setTimer: (run, ms) => window.setTimeout(run, ms),
    clearTimer: (id) => window.clearTimeout(id as number),
    share,
    readShared: readActivity,
    writeShared: writeActivity,
    onIdle,
  });
  const other = () => tracker.activity();
  const pointer = () => tracker.activity("pointer");
  const visibility = () => {
    if (document.visibilityState === "visible") tracker.check();
  };
  const options = { capture: true, passive: true } as const;
  for (const type of ["keydown", "pointerdown", "wheel", "scroll", "touchstart"] as const) window.addEventListener(type, other, options);
  window.addEventListener("pointermove", pointer, options);
  document.addEventListener("visibilitychange", visibility);
  return {
    tracker,
    dispose: () => {
      tracker.stop();
      for (const type of ["keydown", "pointerdown", "wheel", "scroll", "touchstart"] as const) window.removeEventListener(type, other, options);
      window.removeEventListener("pointermove", pointer, options);
      document.removeEventListener("visibilitychange", visibility);
    },
  };
}
